import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';

import { SingleFileUploadResult, UserAsset } from '@interfaces';

import { UserAssetsHttpService } from '@core/http/user-assets.http.service';
import { FileHandlerService } from '@core/services/file-handler.service';
import { StackedMessagesService, TypedStackedMessageConfig } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import * as actions from './user-assets.actions';

type UserAssetsUploadResult = {
  errorMessage: any;
  uploaded: boolean;
  userAsset: UserAsset;
};

@Injectable()
export class UserAssetsEffects {
  private readonly actions$ = inject(Actions);
  private readonly userAssetsHttp = inject(UserAssetsHttpService);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly stackedMessages = inject(StackedMessagesService);

  private showError(config: TypedStackedMessageConfig): void {
    this.stackedMessages.showError(config);
  }

  removeMyAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeMyAsset),
      switchMap(({ opts }) => {
        return this.userAssetsHttp.removeMyAsset({ userAssetId: opts.userAssetId }).pipe(
          map(() => {
            return actions.removeMyAssetSuccess({ opts });
          }),
          catchError(() => {
            this.stackedMessages.showError({ message: 'NT3.ERROR_REMOVING_ASSET' });
            return of(actions.removeMyAssetError({ errorRemovingAssetId: opts.userAssetId }));
          })
        );
      })
    )
  );

  downloadMyAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.downloadMyAsset),
      mergeMap(({ userAssetId, previewTargetKey }) => {
        return this.userAssetsHttp.downloadMyAsset({ userAssetId }).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return actions.downloadMyAssetSuccess({ userAssetId });
            } else {
              return actions.downloadMyAssetError({ userAssetId });
            }
          }),
          catchError(() => {
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            this.stackedMessages.showError({ message: 'COMMON.ERROR.DOWNLOADING_ATTACHMENT' });
            return of(actions.downloadMyAssetError({ userAssetId }));
          })
        );
      })
    )
  );

  uploadPreSurveyAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.uploadPreSurveyAttachments),
      concatMap(({ opts }) => {
        const { userProcessId, files, preSurveyId } = opts;
        const resultsObj: { [key: string]: Observable<SingleFileUploadResult> } = files.reduce(
          (acc, currFile) => {
            acc[currFile.fileOriginalName] = this.userAssetsHttp
              .uploadPreSurveyAttachment({ preSurveyId, userProcessId, file: currFile.file })
              .pipe(
                map((uploadResult): UserAssetsUploadResult => {
                  return { errorMessage: null, uploaded: true, userAsset: uploadResult.attachment };
                }),
                catchError(error => {
                  this.showError({
                    message: 'COMMON.ERROR.ERROR_UPLOADING_FILE',
                    translateParams: { fileName: currFile.fileOriginalName },
                  });
                  const errorMessage = parseError(error, actions.uploadPreSurveyAttachments.type);
                  return of<UserAssetsUploadResult>({
                    errorMessage,
                    uploaded: false,
                    userAsset: { fileOriginalName: currFile.fileOriginalName } as UserAsset,
                  });
                })
              );
            return acc;
          },
          {} as { [key: string]: Observable<SingleFileUploadResult> }
        );

        return forkJoin(resultsObj).pipe(
          map(uploadedFiles => {
            return actions.uploadPreSurveyAttachmentsSuccess({ opts: { preSurveyId, userProcessId, uploadedFiles } });
          }),
          catchError(() => {
            return of(actions.uploadPreSurveyAttachmentsError());
          })
        );
      })
    )
  );
}
